import * as React from 'react';
import { STATE } from 'app/constants';
import { AdministrationUserEditProps, UserDataFactoryProps } from './types';
import { defaultSerializeMappings } from 'app/infrastructure/simple-form/serialize';
import { serializeAccessData, serializeTechnicalUser } from 'app/area/administration/edit/serialize';
import { addUserValidationConfig, editUserValidationConfig } from './user-edit-configuration';
import { PersonActionType } from 'app/area/administration/shared/types';
import { accessDataValidation } from 'app/area/administration/edit/access-data-validation';
import { User } from 'app/components/access-panel/shared/types';

// eslint:disable:max-func-body-length
export const userEditFactory = (WrappedComponent:
  React.ComponentClass<AdministrationUserEditProps> | React.FC<AdministrationUserEditProps>) => {
  return class extends React.Component<AdministrationUserEditProps> {
    public static statePath: string = STATE.administrationEdit;

    private addUser = () => {
      const {
        params: { actionType } = {}, validate, serialize, addUser, validateCustomData, translation = {},
        accessData, extraInfoCodes, data: originalData, sharedState: { access: { adminPanel } }
      } = this.props;

      const currentData = serialize(defaultSerializeMappings);

      const data = {
        ...currentData,
        ...serializeAccessData(accessData, actionType, extraInfoCodes, originalData, adminPanel),
        ...serializeTechnicalUser(originalData, currentData, actionType)
      };

      const accessDataErrors = accessDataValidation(accessData, translation.operation_required_message);

      accessDataErrors
        ? validateCustomData(accessDataErrors)
        : validate(addUserValidationConfig, () => addUser(data), translation.validation_error);
    };

    private performUpdateUser = () => {
      const {
        params: { actionType } = {}, validate, serialize, updateUser, validateCustomData, id, translation = {},
        accessData, extraInfoCodes, data: originalData,
        isTechnicalUserWarningShown, showTechnicalUserWarning, sharedState: { access: { adminPanel } }
      } = this.props;

      const currentData = serialize(defaultSerializeMappings);

      const serializedData = {
        ...currentData,
        ...serializeAccessData(accessData, actionType, extraInfoCodes, originalData, adminPanel),
        ...serializeTechnicalUser(originalData, currentData, actionType)
      } as User;

      const accessDataErrors = accessDataValidation(accessData, translation.operation_required_message);

      accessDataErrors
        ? validateCustomData(accessDataErrors)
        : validate(editUserValidationConfig, () => {

          if (serializedData.technical && !isTechnicalUserWarningShown) {
            showTechnicalUserWarning();
            return;
          }

          updateUser(id, serializedData);
        }, translation.validation_error);
    };

    private duplicateUser = () => {
      const {
        params: { actionType } = {}, validate, serialize, duplicateUser, validateCustomData, translation = {},
        accessData, extraInfoCodes, data: originalData, sharedState: { access: { adminPanel } }
      } = this.props;

      const currentData = serialize(defaultSerializeMappings);

      const data = {
        ...serialize(defaultSerializeMappings),
        ...serializeAccessData(accessData, actionType, extraInfoCodes, originalData, adminPanel),
        ...serializeTechnicalUser(originalData, currentData, actionType)
      };

      const accessDataErrors = accessDataValidation(accessData, translation.operation_required_message);

      accessDataErrors
        ? validateCustomData(accessDataErrors)
        : validate(addUserValidationConfig, () => duplicateUser(data), translation.validation_error);
    };

    private create = (): AdministrationUserEditProps => {
      const { id, loadUser, data, translation = {} } = this.props;

      const conf: { [key: string]: UserDataFactoryProps } = {
        [PersonActionType.add]: {
          title: translation.new_user,
          saveButtonText: translation.add_user,
          showUserEditMenu: false,
          showPersonId: true,
          showUserId: false,
          onSave: this.addUser
        },

        [PersonActionType.edit]: {
          title: data && data.personId,
          saveButtonText: translation.update_user,
          showUserEditMenu: true,
          showUserId: !!id || id === 0,
          onSave: this.performUpdateUser,
          onLoadUser: () => loadUser(id)
        },

        [PersonActionType.duplicate]: {
          title: translation.duplicate_user_title,
          saveButtonText: translation.duplicate_user_save_text,
          showUserEditMenu: false,
          showPersonId: true,
          showUserId: false,
          hideDetails: true,
          onSave: this.duplicateUser,
          onLoadUser: () => loadUser(id, { personId: '', name: '', emailAddress: '', alternativeIds: undefined })
        }
      };

      return { ...this.props, ...conf[this.props.params.actionType] } as AdministrationUserEditProps;
    };

    public render() {
      return <WrappedComponent key={this.props.params.actionType} {...this.create()} />;
    }
  };
};
